import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/**
 * SW2-2239 — the single source of truth for what date a certification signature line pre-fills with,
 * and the bounds its picker allows.
 *
 * This replaces SEVEN hand-rolled copies of `new Date(doi.getTime() + doi.getTimezoneOffset() * 60000)`
 * spread across the Certification tab, the three bulk certify modals and `inspectorMinDate`. They had
 * drifted: one commit introduced the idiom in two places (39badeebd, 2023), then 64608ca5e and
 * 23331dfe2 corrected only the Certification tab.
 *
 * ## Why this keeps coming back
 *
 * The offset idiom is not itself wrong — for a bare `"YYYY-MM-DD"` it yields local midnight of the
 * right day in ANY browser timezone. The defect is a MIXED BASELINE:
 * `InspectionCertificationContext` built the DOI as a UTC-midnight instant (`new Date(doi.getTime())`)
 * while building finding-completion dates as LOCAL midnight from `getUTC*` parts. Comparing those, a
 * required finding completed before the DOI returned the UTC-midnight value, which renders as the day
 * BEFORE in Denver and correctly in IST. The dev team is in India and the users are in the US, so the
 * code looked right locally and was wrong in production.
 *
 * The fix is structural rather than arithmetic: everything goes through `toCalendarDate`, so both
 * operands of every comparison share a baseline by construction and the bug becomes unexpressible.
 *
 * Uses **dayjs**, per `CLAUDE.md` and `docs/architecture/adr/004-dates.md` ("prefer dayjs; do not add
 * new moment / date-fns imports"). This is the repo's first dayjs-timezone usage, deliberately confined
 * to this module.
 */

/** Matches the backend's `dateService.getDefaultTimezone()`. */
export const DEFAULT_PROJECT_TIMEZONE = 'America/Denver';

export type DefaultDateLogic = 'doi' | 'current';

export type CertSignatureGroup = 'inspection' | 'compliance' | 'acknowledgement';

/**
 * What a NULL `signature_lines.default_date_logic` means, per group.
 *
 * 1,313 of 1,547 signature lines (85%) have this column NULL, so this table — not the explicit
 * settings — is the de facto behaviour for almost all data. Each value reproduces what ships TODAY, and
 * is deliberately NOT internally consistent:
 *
 *   - `inspection` → doi: the inspection group ignores the column entirely today (`inspectorDefaultDate`
 *     never reads it) and always uses the DOI.
 *   - `compliance` → doi: matches what the server already stores for unconfigured templates (1,504 live
 *     projects across 35 tenants). Treating NULL as `current` would flip all of them to today's date and
 *     re-open SW2-2131, which required a 194-inspection manual data fix.
 *   - `acknowledgement` → current: what the form prefills today for a NULL line.
 *
 * Do not "harmonise" these — only a line with an explicit setting should change behaviour.
 * Mirrors `GROUP_FALLBACK_LOGIC` in the backend's `certificationDateService`.
 */
export const GROUP_FALLBACK_LOGIC: Record<CertSignatureGroup, DefaultDateLogic> =
  {
    inspection: 'doi',
    compliance: 'doi',
    acknowledgement: 'current',
  };

/**
 * Pick the timezone that decides which calendar day a date falls on.
 *
 * Never `dayjs.tz.guess()` — that is the browser timezone, i.e. the bug. A compliance record should
 * reflect the site's day, not wherever the signer happened to open their laptop, and keying off the
 * project makes behaviour identical for a developer in Kolkata and an inspector in Denver.
 *
 * Falls back with a warning rather than throwing: a project with a missing or unrecognised timezone
 * must still be signable.
 */
export function resolveProjectTimezone(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    if (!candidate) continue;

    // dayjs throws on an unknown zone; treat that as "not usable" rather than crashing the form.
    try {
      dayjs().tz(candidate);

      return candidate;
    } catch {
      // fall through to the next candidate
    }
  }

  // eslint-disable-next-line no-console
  console.warn(
    'certDefaultDate: no usable project timezone, falling back',
    { candidates, using: DEFAULT_PROJECT_TIMEZONE },
  );

  return DEFAULT_PROJECT_TIMEZONE;
}

/** A line's effective logic — its own column value, or the group's fallback when NULL. */
export function resolveDefaultDateLogic(
  group: CertSignatureGroup,
  lineLogic?: string | null,
): DefaultDateLogic {
  if (lineLogic === 'doi' || lineLogic === 'current') {
    return lineLogic;
  }

  return GROUP_FALLBACK_LOGIC[group] ?? 'current';
}

/**
 * THE conversion primitive. Reduce any date-ish value to **local midnight of the calendar day it
 * represents**, with the project timezone deciding which day that is.
 *
 * Four ordered branches, each chosen to preserve today's behaviour for data that already exists:
 *
 *   1. falsy → null
 *   2. a bare `YYYY-MM-DD` → those literal parts, NO timezone maths. Covers `inspections.created_date`
 *      (DATEONLY, so it arrives as a string) and everything the offline path wrote via `getDateOnly`.
 *   3. an instant at exactly 00:00:00.000 **UTC** → its UTC parts. These are date-only values that
 *      round-tripped through a `Date`; this is what today's `getUTC*` reads already do, so no change.
 *   4. anything else → a real timestamp, read in the project timezone. Covers a `date_completed`
 *      written outside the `getDateOnly` path (the column is a timestamptz, so this happens).
 *
 * Always returns **local midnight**, and that return type is load-bearing: `lib-form`'s `DateControl`
 * hands the value straight to react-datepicker as `selected`, which renders in browser-local time.
 * Local midnight is the only representation that displays as the same calendar day in every timezone.
 */
export function toCalendarDate(
  value: string | Date | null | undefined,
  timezone: string,
): Date | null {
  if (!value) return null;

  const fromParts = (year: number, month: number, day: number) =>
    new Date(year, month - 1, day);

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const bareDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);

    if (bareDay) {
      return fromParts(
        Number(bareDay[1]),
        Number(bareDay[2]),
        Number(bareDay[3]),
      );
    }
  }

  const asUtc = dayjs.utc(value);

  if (!asUtc.isValid()) return null;

  // A date-only value that became a Date sits at UTC midnight; its UTC parts are the intended day.
  if (
    asUtc.hour() === 0 &&
    asUtc.minute() === 0 &&
    asUtc.second() === 0 &&
    asUtc.millisecond() === 0
  ) {
    return fromParts(asUtc.year(), asUtc.month() + 1, asUtc.date());
  }

  const inZone = dayjs.utc(value).tz(timezone);

  return fromParts(inZone.year(), inZone.month() + 1, inZone.date());
}

/** `MM-DD-YYYY`, the format the app displays and `DateControl` emits. */
export function formatCalendarDate(value: string | Date): string {
  const parsed = value instanceof Date ? dayjs(value) : dayjs(value);

  return parsed.format('MM-DD-YYYY');
}

export type CertLineDateInput = {
  group: CertSignatureGroup;
  /** THIS line's own `default_date_logic`. Never the group's first line's. */
  defaultDateLogic?: string | null;
  inspectionDOI: string | Date | null | undefined;
  timezone: string;
  /** Injected so tests are deterministic. */
  now?: Date;
  /** Compliance only: max(date_completed) of required-for-cert findings, as a calendar date. */
  requiredFindingCompletion?: Date | null;
  /** Tenant guardrail; outranks the template's logic. */
  disableInspectionBackdating?: boolean;
  /** Acknowledgement only: CDOT-family templates default to DOI. */
  isCDOT?: boolean;
};

/**
 * The date a line should pre-fill with.
 *
 * The compliance closure check is nested INSIDE the `doi` branch — that is the whole point of this
 * ticket. The bulk modals always had this ordering right; the Certification tab hoisted the check above
 * the config gate, so "Current Date" was unreachable and the two surfaces disagreed.
 */
export function getCertLineDefaultDate(input: CertLineDateInput): Date {
  const {
    group,
    defaultDateLogic,
    inspectionDOI,
    timezone,
    now,
    requiredFindingCompletion,
    disableInspectionBackdating,
    isCDOT,
  } = input;

  const today = toCalendarDate(now ?? new Date(), timezone) as Date;

  // Tenant guardrail first — matches the Certification tab's existing precedence.
  if (disableInspectionBackdating) return today;

  let logic = resolveDefaultDateLogic(group, defaultDateLogic);

  if (group === 'acknowledgement' && isCDOT) logic = 'doi';

  if (logic === 'current') return today;

  const doi = toCalendarDate(inspectionDOI, timezone);

  // Matches the bulk modals' `|| new Date()` when the DOI is unknown.
  if (!doi) return today;

  if (
    group === 'compliance' &&
    requiredFindingCompletion &&
    requiredFindingCompletion.getTime() > doi.getTime()
  ) {
    return requiredFindingCompletion;
  }

  return doi;
}

/**
 * The picker bounds for a line.
 *
 * `maxDate` is always today: no certification may be dated in the future. `minDate` follows the line's
 * logic:
 *
 *   | logic                           | min                     |
 *   | doi, required findings closed    | latest required closure |
 *   | doi, no required findings        | DOI                     |
 *   | current                          | DOI                     |
 *   | backdating disabled (any logic)  | today                   |
 *
 * Derived from the same input as `getCertLineDefaultDate`, so `minDate <= default <= maxDate` holds by
 * construction. Without that, the form could pre-fill a date its own picker rejects — which blocks
 * signing outright rather than merely recording the wrong day.
 *
 * Before this, `default_date_logic` never influenced the bounds at all: `getCertFormDateSchema` /
 * `getDateInput` in lib-certification do not read it, and four of the five callers passed a bare
 * `new Date(certState.minCertificationDate)` with no timezone correction, permitting one calendar day
 * earlier than the server's stated minimum in every US zone.
 */
export function getCertLineDateBounds(input: CertLineDateInput): {
  minDate: Date;
  maxDate: Date;
} {
  const { group, inspectionDOI, timezone, now, requiredFindingCompletion } =
    input;

  const today = toCalendarDate(now ?? new Date(), timezone) as Date;

  if (input.disableInspectionBackdating) {
    return { minDate: today, maxDate: today };
  }

  const doi = toCalendarDate(inspectionDOI, timezone);

  if (!doi) return { minDate: today, maxDate: today };

  let minDate = doi;

  if (
    group === 'compliance' &&
    requiredFindingCompletion &&
    requiredFindingCompletion.getTime() > doi.getTime()
  ) {
    minDate = requiredFindingCompletion;
  }

  // A future-dated inspection would otherwise give min > max and lock the picker to an empty range.
  if (minDate.getTime() > today.getTime()) minDate = today;

  return { minDate, maxDate: today };
}
